/* A Generic Modal Component
  Desktop: Displays a white modal on a dark background
  Mobile: Full-page white modal
  Props:
  -> isOpen: Bool - whether the modal is open or not.
  -> onClose: Function - called when modal close triggered.
  -> isCloseButtonHidden: Bool - whether to show the close button in the top right corner of the modal.
  -> openSpeed: Number, closeSpeed: Number - animation speeds of modal (in ms).

  Example usage:
  export const Modal = ReactModal(ModalContent: ReactComponent, defaultProps?: Object)
  return (<Modal onClose={Function} isOpen={Bool}/>)

  Can also add custom styles to the body of the modal like so:
  const StyledModal = styled(Modal)`
    width: 400px;
  `
  (these styles automatically get passed down as the className prop)
*/

import { useState, useEffect, useRef, PointerEvent } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Portal } from 'react-portal';
import { useDidUpdateEffect } from 'ui_system/hooks/useDidUpdateEffect';
import CloseIcon from 'views/ui/icons/close-icon';
import { overlays } from 'ui_system/compass/utils';

import { Theme } from '../theme';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const zoomIn = keyframes`
  from {
    opacity: 0;
    transform: scale3d(0.7, 0.7, 0.7);
  }
  to {
    opacity: 1;
  }
`;

const zoomOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    transform: scale3d(0.7, 0.7, 0.7);
  }
`;

interface StyledProps {
  animationName: string;
  animationSpeed: string;
  padding?: string;
}

const Background = styled.div<StyledProps>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: grid;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;

  background: ${overlays.primary};

  z-index: 1000000;

  animation-name: ${(props) => props.animationName};
  animation-duration: ${(props) => props.animationSpeed};
  animation-fill-mode: both;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    overflow-y: auto;
  }
`;

const ModalMain = styled.div<StyledProps>`
  box-sizing: border-box;
  background: white;
  position: relative;

  animation-name: ${(props) => props.animationName};
  animation-duration: ${(props) => props.animationSpeed};
  animation-fill-mode: both;

  /* Needs to be 100vh rather than 100% for iOS */
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  padding: 20px;
  margin: auto;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    min-height: initial;
    width: 600px;
    max-width: 90vw;
    padding: ${(props) => (props.padding ? props.padding : '40px 50px 50px')};
    margin: 50px auto;
  }
`;

interface CloseProps {
  isMobileCloseButtonHidden?: boolean;
}

export const CloseButton = styled.div<CloseProps>`
  /* Temporary hack to use this close button with the old modal until we replace that modal */
  display: ${(props) => (props.isMobileCloseButtonHidden ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;
  border: 1px solid white;
  border-radius: 50%;
  height: 56px;
  width: 56px;

  cursor: pointer;
  z-index: 1000;

  position: fixed;
  top: 0;
  right: 0;
  background: white;

  svg {
    transform: scale(1.333);
    fill: #999;
  }

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    display: flex;
    position: absolute;
    height: 32px;
    width: 32px;
    top: -16px;
    right: -16px;
    background: ${Theme.colors.black};
    svg {
      transform: scale(1.2);
      fill: white;
    }
  }
`;

interface ModalProps {
  children: JSX.Element;
  className: string;
  onClose(): void;
  isCloseButtonHidden: boolean;
  isClosing: boolean;
  openSpeed: number;
  closeSpeed: number;
  isBackgroundClickable: boolean;
  padding?: string;
}

function Modal(props: ModalProps) {
  const {
    children,
    className,
    onClose,
    isCloseButtonHidden,
    isClosing,
    openSpeed,
    closeSpeed,
    isBackgroundClickable,
    padding,
  } = props;

  const bgRef = useRef<HTMLDivElement>(null);
  const allowClose = useRef(false);
  function setAllowClose(bool: boolean) {
    allowClose.current = bool;
  }

  useEffect(() => {
    window.document.body.style.overflowY = 'hidden';
    return () => {
      window.document.body.style.overflowY = 'initial';
    };
  }, []);

  const animationSpeed = isClosing ? `${closeSpeed}ms` : `${openSpeed}ms`;
  const isCloseButtonVisible = !isCloseButtonHidden;

  return (
    <Portal>
      <Background
        ref={bgRef}
        onPointerDown={(e: PointerEvent) => {
          if (!isBackgroundClickable) return;

          // Handles clicking on vertical scroll bar:
          if (e.clientX >= (e.target as HTMLDivElement).clientWidth) {
            setAllowClose(false);
          } else {
            setAllowClose(true);
          }
        }}
        onPointerUp={(e: PointerEvent) => {
          if (!isBackgroundClickable) return;

          if (
            bgRef.current === e.target &&
            allowClose.current &&
            e.clientX < (e.target as HTMLDivElement).clientWidth
          ) {
            onClose();
          }
        }}
        animationName={isClosing ? fadeOut : fadeIn}
        animationSpeed={animationSpeed}
      >
        <ModalMain
          className={className}
          onPointerDown={(e) => {
            e.stopPropagation();
            setAllowClose(false);
          }}
          animationName={isClosing ? zoomOut : zoomIn}
          animationSpeed={animationSpeed}
          padding={padding}
          data-testid="modal-main-overlay"
        >
          {isCloseButtonVisible && (
            <CloseButton onClick={onClose} data-testid="close-modal">
              <CloseIcon />
            </CloseButton>
          )}
          {children}
        </ModalMain>
      </Background>
    </Portal>
  );
}

interface ReactModalProps {
  isOpen?: boolean;
  isCloseButtonHidden?: boolean;
  openSpeed?: number;
  closeSpeed?: number;
  isBackgroundClickable?: boolean;
  className?: string;
  padding?: string;
}

interface ModalWrapperProps extends ReactModalProps {
  isOpen: boolean;
  onClose(): void;
}

export function ReactModal<CustomProps>(
  ModalContent: (props: any) => JSX.Element,
  defaultProps: ReactModalProps = {}
): (props: CustomProps & ModalWrapperProps) => JSX.Element | null {
  const defaults = {
    className: '',
    isOpen: false,
    isCloseButtonHidden: false,
    openSpeed: 260,
    closeSpeed: 200,
    isBackgroundClickable: true,
    ...defaultProps,
  };

  function ModalWrapper(props: CustomProps & ModalWrapperProps) {
    const {
      className = defaults.className,
      isOpen = defaults.isOpen,
      onClose,
      isCloseButtonHidden = defaults.isCloseButtonHidden,
      openSpeed = defaults.openSpeed,
      closeSpeed = defaults.closeSpeed,
      isBackgroundClickable = defaults.isBackgroundClickable,
      padding = defaults.padding,
      ...otherProps
    } = props;

    const [isClosing, setIsClosing] = useState(false);
    const [isModalOpen, setModalOpen] = useState(isOpen);

    const handleClose = () => {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setModalOpen(false);
      }, closeSpeed);
    };

    // It needs to react to the change in isOpen since this state is controlled from above.
    // Otherwise it would cause the modal to immediately unmount, losing the animation.
    useDidUpdateEffect(() => {
      if (isOpen) {
        setModalOpen(true);
      } else {
        handleClose();
      }
    }, [isOpen]);

    return isModalOpen ? (
      <Modal
        className={className}
        onClose={onClose}
        isCloseButtonHidden={isCloseButtonHidden}
        isClosing={isClosing}
        openSpeed={openSpeed}
        closeSpeed={closeSpeed}
        isBackgroundClickable={isBackgroundClickable}
        padding={padding}
      >
        <ModalContent onClose={onClose} {...otherProps} />
      </Modal>
    ) : null;
  }

  return ModalWrapper;
}
